import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  LogOut, 
  FileText, 
  Search,
  Filter,
  Trash2,
  ExternalLink,
  Calendar,
  Upload,
  X,
  Settings,
  KeyRound,
  ChevronDown,
  Lock,
  Unlock,
  Edit,
  Eye,
  Mail,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import ChangePasswordModal from '../common/ChangePasswordModal';

const InstructorDashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('submissions'); // 'submissions' or 'creation'
  const [activeTab, setActiveTab] = useState('partial');
  const [submissions, setSubmissions] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [isCreateClassTestOpen, setIsCreateClassTestOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [batches, setBatches] = useState([]);
  const [assignmentData, setAssignmentData] = useState({
    assigned_batches: [],
    start_date: '',
    end_date: ''
  });
  const [newClassTest, setNewClassTest] = useState({
    title: '',
    type: 'class_test',
    task_configuration: 'full',
    task1_prompt: '',
    task1_image: null,
    task1_image_width: 500,
    task1_image_height: 300,
    task2_prompt: '',
    duration_minutes: 60,
    assigned_batches: [],
    assigned_instructors: [],
    unlock_time: '',
    lock_time: ''
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const handleCreateClassTest = async () => {
    // Validation
    if (!newClassTest.title.trim()) {
      toast.error('Please enter test title');
      return;
    }

    // Validate based on task configuration
    if (newClassTest.task_configuration === 'full') {
      if (!newClassTest.task1_prompt.trim()) {
        toast.error('Please enter Task 1 question');
        return;
      }
      if (!newClassTest.task2_prompt.trim()) {
        toast.error('Please enter Task 2 question');
        return;
      }
    } else if (newClassTest.task_configuration === 'task_1_only') {
      if (!newClassTest.task1_prompt.trim()) {
        toast.error('Please enter Task 1 question');
        return;
      }
    } else if (newClassTest.task_configuration === 'task_2_only') {
      if (!newClassTest.task2_prompt.trim()) {
        toast.error('Please enter Task 2 question');
        return;
      }
    }

    try {
      const payload = {
        ...newClassTest,
        task1_prompt: newClassTest.task_configuration !== 'task_2_only' ? newClassTest.task1_prompt : '',
        task2_prompt: newClassTest.task_configuration !== 'task_1_only' ? newClassTest.task2_prompt : '',
        start_date: newClassTest.unlock_time || null,
        end_date: newClassTest.lock_time || null,
      };

      if (selectedTest) {
        // Update existing test
        await axios.put(`/instructor/tests/${selectedTest.id}`, payload);
        toast.success('Test updated successfully!');
      } else {
        // Create new test
        await axios.post('/instructor/tests', payload);
        toast.success('Class test created successfully!');
      }
      
      setIsCreateClassTestOpen(false);
      setSelectedTest(null);
      
      // Reset form
      setNewClassTest({
        title: '',
        type: 'class_test',
        task_configuration: 'full',
        task1_prompt: '',
        task1_image: null,
        task1_image_width: 500,
        task1_image_height: 300,
        task2_prompt: '',
        duration_minutes: 60,
        assigned_batches: [],
        assigned_instructors: [],
        unlock_time: '',
        lock_time: ''
      });

      // Refresh tests list
      fetchTests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create class test');
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchTests();
    fetchBatches();
    fetchInstructors();
  }, []);

  // Check user status periodically - logout if user is paused/deactivated
  // Also refresh user permissions (like can_create_tests) from server
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const response = await axios.get('/auth/me');
        if (!response.data.is_active) {
          toast.error('Your account has been paused by admin');
          logout();
        } else {
          // Refresh user data to get updated permissions
          await refreshUser();
        }
      } catch (error) {
        console.error('Failed to check user status:', error);
      }
    };

    checkUserStatus();
    const interval = setInterval(checkUserStatus, 300000); // Check every 5 minutes (reduced from 30s to lower server load)
    return () => clearInterval(interval);
  }, [logout, refreshUser]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get('/instructor/submissions');
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await axios.get('/instructor/tests');
      setTests(response.data);
    } catch (error) {
      toast.error('Failed to fetch tests');
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/batches');
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const fetchInstructors = async () => {
    // Only admins can fetch the full users list. Instructors don't have access to /admin/users (403).
    if (user?.role !== 'admin') {
      return;
    }
    try {
      const response = await axios.get('/admin/users');
      const instructorsList = response.data.filter(u => u.role === 'instructor');
      setInstructors(instructorsList);
    } catch (error) {
      // Silent fail - not critical if instructors list unavailable
      console.log('Could not fetch instructors list');
    }
  };

  const handleOpenAssignment = (test) => {
    setSelectedTest(test);
    setAssignmentData({
      assigned_batches: test.assigned_batches || [],
      start_date: test.start_date ? test.start_date.split('T')[0] : '',
      end_date: test.end_date ? test.end_date.split('T')[0] : ''
    });
    setIsAssignDialogOpen(true);
  };

  const handleAssignTest = async () => {
    if (!selectedTest) return;

    try {
      await axios.post(`/instructor/tests/${selectedTest.id}/assign`, assignmentData);
      toast.success('Test assigned successfully!');
      setIsAssignDialogOpen(false);
      fetchTests(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign test');
    }
  };

  // Text formatting helpers
  const insertFormatting = (field, format) => {
    const textarea = document.getElementById(field);
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    let formattedText;
    let newCursorPos;

    switch(format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        newCursorPos = start + 2 + selectedText.length;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        newCursorPos = start + 1 + selectedText.length;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        newCursorPos = start + 2 + selectedText.length;
        break;
      default:
        return;
    }

    const newText = text.substring(0, start) + formattedText + text.substring(end);
    setNewClassTest({ ...newClassTest, [field]: newText });

    // Set cursor position after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const groupSubmissionsByDate = (submissions) => {
    const grouped = {};
    
    submissions.forEach(sub => {
      const date = sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('en-GB') : 'Not submitted';
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(sub);
    });
    
    return Object.entries(grouped).sort((a, b) => {
      if (a[0] === 'Not submitted') return 1;
      if (b[0] === 'Not submitted') return -1;
      return new Date(b[0].split('/').reverse().join('-')) - new Date(a[0].split('/').reverse().join('-'));
    });
  };

  const groupSubmissionsByTest = (submissions) => {
    const grouped = {};
    
    submissions.forEach(sub => {
      const testId = sub.test_id;
      if (!grouped[testId]) {
        grouped[testId] = {
          testTitle: sub.test_title || 'Untitled Test',
          submissions: []
        };
      }
      grouped[testId].submissions.push(sub);
    });
    
    return grouped;
  };

  const handleViewResult = async (submissionId, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      const response = await axios.get(`/instructor/submission/${submissionId}/result-token`);
      const resultToken = response.data.result_token;
      
      if (resultToken) {
        // Open result page in new tab
        window.open(`/result/${resultToken}`, '_blank');
      } else {
        toast.error('Result token not found');
      }
    } catch (error) {
      console.error('Failed to get result token:', error);
      toast.error('Failed to open result page');
    }
  };

  const handleResendEmail = async (submissionId, candidateName, e) => {
    e.stopPropagation();
    try {
      toast.loading('Sending email...', { id: 'resend-email' });
      const response = await axios.post(`/instructor/submissions/${submissionId}/resend-email`);
      toast.success(`Email sent to ${candidateName}!`, { id: 'resend-email' });
    } catch (error) {
      console.error('Failed to resend email:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      toast.error(errorMsg, { id: 'resend-email' });
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = 
      sub.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.candidate_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.candidate_no?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    
    // Filter by test type based on active tab
    let matchesTestType = true;
    if (activeTab === 'partial') {
      matchesTestType = sub.test_type === 'partial_test';
    } else if (activeTab === 'batch') {
      matchesTestType = sub.test_type === 'batch_hw';
    } else if (activeTab === 'class') {
      matchesTestType = sub.test_type === 'class_test';
    }
    
    return matchesSearch && matchesStatus && matchesTestType;
  });

  const toggleSubmissionSelection = (submissionId) => {
    setSelectedSubmissions(prev => 
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSubmissions.length === filteredSubmissions.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(filteredSubmissions.map(s => s.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSubmissions.length === 0) {
      toast.error('No submissions selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedSubmissions.length} submissions?`)) {
      return;
    }

    toast.info('Bulk delete feature coming soon');
  };

  const handleToggleLock = async (testId, currentLockStatus) => {
    try {
      const test = tests.find(t => t.id === testId);
      const now = new Date().toISOString();
      
      await axios.put(`/instructor/tests/${testId}`, {
        ...test,
        is_locked: !currentLockStatus,
        end_date: currentLockStatus ? null : now
      });
      
      toast.success(currentLockStatus ? 'Test unlocked successfully!' : 'Test locked successfully!');
      fetchTests();
    } catch (error) {
      console.error('Failed to toggle lock:', error);
      toast.error('Failed to update test lock status');
    }
  };

  const handleDeleteTest = async (testId) => {
    try {
      // Check if test has submitted submissions
      const response = await axios.get(`/instructor/test/${testId}/submissions`);
      const submittedSubmissions = response.data.filter(sub => 
        sub.status === 'submitted' || sub.status === 'in_progress' || sub.status === 'marked'
      );
      
      if (submittedSubmissions.length > 0) {
        const unpublishedCount = submittedSubmissions.filter(sub => sub.status !== 'published').length;
        if (unpublishedCount > 0) {
          toast.error(`Cannot delete test. ${unpublishedCount} submission(s) need to be marked and published first.`);
          return;
        }
      }
      
      // Delete test
      await axios.delete(`/instructor/tests/${testId}`);
      toast.success('Test deleted successfully!');
      fetchTests();
    } catch (error) {
      console.error('Failed to delete test:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete test');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-700 border-blue-200',
      in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      marked: 'bg-purple-100 text-purple-700 border-purple-200',
      published: 'bg-green-100 text-green-700 border-green-200'
    };

    const labels = {
      submitted: 'Submitted',
      in_progress: 'In Progress',
      marked: 'Marked',
      published: 'Published'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const groupedSubmissions = groupSubmissionsByDate(filteredSubmissions);

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 sm:py-3 md:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/radiance-logo.webp" 
                alt="Radiance Education" 
                className="h-8 sm:h-10 md:h-12 w-auto"
              />
              <div>
                <h1 className="text-sm sm:text-base md:text-xl font-bold text-white">Instructor Dashboard</h1>
                <p className="text-xs sm:text-sm text-white opacity-80 truncate max-w-[120px] sm:max-w-none">Welcome, {user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Profile Picture Circle - Clickable */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-white shadow-lg hover:opacity-80 transition-opacity cursor-pointer bg-gradient-to-br from-purple-500 to-pink-500"
                >
                  {user.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                      {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </button>
                
                {showProfileMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40 bg-black bg-opacity-50 animate-fadeIn" 
                      onClick={() => setShowProfileMenu(false)}
                    />
                    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-xl shadow-2xl z-50 overflow-hidden animate-zoomIn">
                      {/* Close Button */}
                      <button
                        onClick={() => setShowProfileMenu(false)}
                        className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all text-white"
                        aria-label="Close"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      
                      {/* Profile Header with large circle */}
                      <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-6 text-center relative overflow-hidden">
                        {/* Background Image with low opacity */}
                        <div 
                          className="absolute inset-0 bg-cover bg-center opacity-15"
                          style={{
                            backgroundImage: 'url(/modal-bg.jpg)',
                            filter: 'blur(2px)'
                          }}
                        />
                        
                        {/* Content */}
                        <div className="relative z-10">
                          <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-white shadow-lg bg-gradient-to-br from-purple-500 to-pink-500 mb-3">
                            {user.profile_picture ? (
                              <img 
                                src={user.profile_picture} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold text-3xl">
                                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                              </div>
                            )}
                          </div>
                          <h2 className="text-xl font-bold text-white">{user.name}</h2>
                          <p className="text-sm text-white opacity-90">{user.email}</p>
                          <p className="text-sm text-white opacity-90 mt-1">{user.designation}</p>
                        </div>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            setShowChangePassword(true);
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-slate-100 rounded-lg flex items-center text-slate-700 transition-colors"
                        >
                          <KeyRound className="w-5 h-5 mr-3 text-blue-600" />
                          <span className="font-medium">Change Password</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            logout();
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-red-50 rounded-lg flex items-center text-red-600 transition-colors mt-1"
                        >
                          <LogOut className="w-5 h-5 mr-3" />
                          <span className="font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)} 
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Card - Hidden, info shown in modal now */}
        {false && (
        <Card className="glass-card mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold" style={{background: 'linear-gradient(135deg, rgb(22, 113, 127) 0%, rgb(58, 143, 151) 100%)'}}>
                {user.name.charAt(0)}
              </div>
              <div className="flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-slate-800">{user.name}</h2>
                <p className="text-xs sm:text-sm text-slate-600">{user.email}</p>
                <p className="text-sm font-medium mt-1" style={{color: 'rgb(22, 113, 127)'}}>{user.designation}</p>
              </div>
              <Button
                onClick={logout}
                variant="outline"
                className="text-red-500 hover:bg-red-50 text-xs sm:text-sm"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Main Tabs: Submission & Creation */}
        <div className="flex gap-2 mb-6 bg-white rounded-lg p-2 shadow-md">
          <button
            className={`flex-1 py-3 px-6 rounded-md transition-all text-base font-semibold ${
              mainTab === 'submissions'
                ? 'text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            style={mainTab === 'submissions' ? {background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'} : {}}
            onClick={() => setMainTab('submissions')}
          >
            Test Submission
          </button>
          {/* Test Creation tab - Only visible if user has permission */}
          {user.can_create_tests && (
            <button
              className={`flex-1 py-3 px-6 rounded-md transition-all text-base font-semibold ${
                mainTab === 'creation'
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              style={mainTab === 'creation' ? {background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'} : {}}
              onClick={() => setMainTab('creation')}
            >
              Test Creation
            </button>
          )}
        </div>

        {/* Submission Tab Content */}
        {mainTab === 'submissions' && (
          <>
        {/* Test Type Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-lg p-2 shadow-sm">
          <button
            className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 text-xs sm:text-base ${
              activeTab === 'partial' 
                ? 'bg-slate-100 text-slate-900 font-semibold' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('partial')}
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Partial Test</span>
            <span className="sm:hidden">Partial</span>
          </button>
          {/* Batch HW tab - temporarily hidden */}
          {false && (
          <button
            className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 text-xs sm:text-base ${
              activeTab === 'batch' 
                ? 'bg-slate-100 text-slate-900 font-semibold' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('batch')}
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Batch HW</span>
            <span className="sm:hidden">Batch</span>
          </button>
          )}
          <button
            className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 text-xs sm:text-base ${
              activeTab === 'class' 
                ? 'bg-slate-100 text-slate-900 font-semibold' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('class')}
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Class Test</span>
            <span className="sm:hidden">Class</span>
          </button>
        </div>

        {/* Class Test Content - Reordered Layout */}
        {activeTab === 'class' && (
          <div className="space-y-4">
            {filteredSubmissions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No class test submissions found</p>
              </div>
            ) : (
              <>
              {/* SECTION 1: Class Test Submissions Table (Moved to Top) */}
              <Card className="glass-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl text-slate-700">Class Test Submissions</CardTitle>
                    <div className="text-sm text-slate-500">
                      {filteredSubmissions.length} submission(s)
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search and Filter Bar */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by name, email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 text-sm"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-48">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="marked">Marked</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Submissions List */}
                  {loading ? (
                    <div className="text-center py-12 text-slate-500">Loading submissions...</div>
                  ) : groupedSubmissions.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p>No class test submissions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {groupedSubmissions.map(([date, subs]) => (
                        <div key={date}>
                          {/* Date Header */}
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-4 h-4 text-slate-500" />
                            <h3 className="text-sm font-semibold text-slate-700">{date}</h3>
                            <div className="flex-1 border-t border-slate-200"></div>
                            <span className="text-xs text-slate-500">{subs.length} submission(s)</span>
                          </div>

                          {/* Desktop Table */}
                          <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Candidate</th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test</th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Email</th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Time</th>
                                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subs.map((submission) => (
                                  <tr key={submission.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3">
                                      <div className="font-medium text-slate-700">{submission.candidate_name}</div>
                                      {submission.candidate_no && (
                                        <div className="text-xs text-slate-500">Batch- {submission.candidate_no}</div>
                                      )}
                                      {submission.assigned_by && (
                                        <div className="text-xs text-purple-600 mt-1">
                                          📌 Assigned by {submission.assigned_by}
                                        </div>
                                      )}
                                      {submission.is_creator && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          📝 Your Test
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="text-sm text-slate-700">{submission.test_title || 'Untitled Test'}</div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{submission.candidate_email}</td>
                                    <td className="px-4 py-3 text-sm text-slate-500">
                                      {submission.submitted_at 
                                        ? new Date(submission.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                        : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      {getStatusBadge(submission.status)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex items-center justify-center gap-2">
                                        {/* Check can_mark to determine if instructor can mark this submission */}
                                        {submission.can_mark ? (
                                          <>
                                            <Button
                                              size="sm"
                                              className="bg-blue-600 hover:bg-blue-700 text-white"
                                              onClick={() => navigate(`/instructor/mark/${submission.id}`)}
                                            >
                                              <ExternalLink className="w-3 h-3 mr-1" />
                                              Open
                                            </Button>
                                            {submission.status === 'published' && (
                                              <>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="border-green-300 text-green-700 hover:bg-green-50"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewResult(submission.id, e);
                                                  }}
                                                  title="View Student Result"
                                                >
                                                  <Eye className="w-3 h-3 mr-1" />
                                                  Result
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                                  onClick={(e) => handleResendEmail(submission.id, submission.candidate_name, e)}
                                                  title="Resend Email to Student"
                                                >
                                                  <Mail className="w-3 h-3 mr-1" />
                                                  Resend
                                                </Button>
                                              </>
                                            )}
                                          </>
                                        ) : (
                                          /* Creator who is NOT assigned can only view results after marking */
                                          submission.status === 'published' ? (
                                            <>
                                              <Button
                                                size="sm"
                                                className="bg-green-600 hover:bg-green-700 text-white"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewResult(submission.id, e);
                                                }}
                                              >
                                                <Eye className="w-3 h-3 mr-1" />
                                                Result
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                                onClick={(e) => handleResendEmail(submission.id, submission.candidate_name, e)}
                                                title="Resend Email to Student"
                                              >
                                                <Mail className="w-3 h-3" />
                                              </Button>
                                            </>
                                          ) : (
                                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                              ⏳ Awaiting Marking
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile Cards */}
                          <div className="md:hidden space-y-3">
                            {subs.map((submission) => (
                              <div key={submission.id} className="bg-white rounded-lg border border-slate-200 p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-slate-700 text-sm">{submission.candidate_name}</div>
                                    <div className="text-xs text-slate-500 mt-1">{submission.test_title || 'Untitled Test'}</div>
                                    <div className="text-xs text-slate-500 truncate mt-1">{submission.candidate_email}</div>
                                    {submission.assigned_by && (
                                      <div className="text-xs text-purple-600 mt-1">
                                        📌 Assigned by {submission.assigned_by}
                                      </div>
                                    )}
                                    {submission.is_creator && (
                                      <div className="text-xs text-blue-600 mt-1">
                                        📝 Your Test
                                      </div>
                                    )}
                                  </div>
                                  <div className="ml-2">
                                    {getStatusBadge(submission.status)}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                  <div className="text-xs text-slate-500">
                                    {submission.submitted_at 
                                      ? new Date(submission.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                      : '-'}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* Check can_mark to determine if instructor can mark this submission */}
                                    {submission.can_mark ? (
                                      <>
                                        <Button
                                          size="sm"
                                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                          onClick={() => navigate(`/instructor/mark/${submission.id}`)}
                                        >
                                          <ExternalLink className="w-3 h-3 mr-1" />
                                          Open
                                        </Button>
                                        {submission.status === 'published' && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-green-300 text-green-700 hover:bg-green-50 text-xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewResult(submission.id, e);
                                            }}
                                          >
                                            Result
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      submission.status === 'published' ? (
                                        <Button
                                          size="sm"
                                          className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewResult(submission.id, e);
                                          }}
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          Result
                                        </Button>
                                      ) : (
                                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                          ⏳ Awaiting Marking
                                        </span>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* SECTION 2: Test-wise Grouped Cards (Moved to Bottom) */}
              <Card className="glass-card mt-6">
                <CardHeader className="bg-slate-50">
                  <CardTitle className="text-xl text-slate-700">Submissions by Test</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {Object.entries(groupSubmissionsByTest(filteredSubmissions)).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="text-sm">No test-wise submissions to display</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(groupSubmissionsByTest(filteredSubmissions)).map(([testId, testData]) => (
                        <Card key={testId} className="border border-slate-200 shadow-sm">
                          <CardHeader className="py-4 bg-slate-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-lg text-slate-700">{testData.testTitle}</CardTitle>
                                <p className="text-sm text-slate-500 mt-1">{testData.submissions.length} submission(s)</p>
                              </div>
                              <div className="flex gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  testData.submissions.filter(s => s.status === 'published').length === testData.submissions.length
                                    ? 'bg-green-100 text-green-700'
                                    : testData.submissions.some(s => s.status === 'submitted')
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {testData.submissions.filter(s => s.status === 'published').length === testData.submissions.length
                                    ? 'All Published'
                                    : `${testData.submissions.filter(s => s.status === 'submitted').length} Pending`}
                                </span>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-0">
                            <div className="divide-y divide-slate-100">
                              {testData.submissions.map((sub) => (
                                <div
                                  key={sub.id}
                                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                                  onClick={() => navigate(`/instructor/marking/${sub.id}`)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3">
                                        <h4 className="font-medium text-slate-800">
                                          {sub.student_name || sub.candidate_name}
                                        </h4>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          sub.status === 'published' ? 'bg-green-100 text-green-700 border border-green-200' :
                                          sub.status === 'marked' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                                          'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                        }`}>
                                          {sub.status === 'published' ? 'Published' : sub.status === 'marked' ? 'Marked' : 'Pending'}
                                        </span>
                                        {sub.status === 'published' && (
                                          <button
                                            onClick={(e) => handleViewResult(sub.id, e)}
                                            className="ml-2 p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                            title="View Result Page"
                                          >
                                            <ExternalLink className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-500 mt-1">
                                        {sub.candidate_email} {sub.candidate_no && `• Batch: ${sub.candidate_no}`}
                                      </p>
                                      {sub.submitted_at && (
                                        <p className="text-xs text-slate-400 mt-1">
                                          Submitted: {new Date(sub.submitted_at).toLocaleString('en-GB')}
                                        </p>
                                      )}
                                    </div>
                                    {sub.overall_band && (
                                      <div className="ml-4">
                                        <div className="text-center">
                                          <div className="text-2xl font-bold text-slate-700">{sub.overall_band}</div>
                                          <div className="text-xs text-slate-500">Band</div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              </>
            )}
          </div>
        )}

        {/* Partial Test Content */}
        {activeTab === 'partial' && (
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl text-slate-700">Writing Submissions</CardTitle>
                <div className="text-sm text-slate-500">
                  {filteredSubmissions.length} submission(s)
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Filter Bar */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 text-sm"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="marked">Marked</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submissions List Grouped by Date */}
              {loading ? (
                <div className="text-center py-12 text-slate-500">Loading submissions...</div>
              ) : groupedSubmissions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No submissions found</div>
              ) : (
                <div className="space-y-6">
                  {groupedSubmissions.map(([date, subs]) => (
                    <div key={date}>
                      {/* Date Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        <h3 className="text-sm font-semibold text-slate-700">{date}</h3>
                        <div className="flex-1 border-t border-slate-200"></div>
                        <span className="text-xs text-slate-500">{subs.length} submission(s)</span>
                      </div>

                      {/* Submissions - Desktop Table */}
                      <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="w-10 px-4 py-3">
                                <input 
                                  type="checkbox"
                                  onChange={toggleSelectAll}
                                  checked={selectedSubmissions.length === filteredSubmissions.length && filteredSubmissions.length > 0}
                                  className="rounded border-slate-300"
                                />
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Candidate</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Email</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Time</th>
                              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subs.map((submission) => (
                              <tr key={submission.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-3">
                                  <input 
                                    type="checkbox"
                                    checked={selectedSubmissions.includes(submission.id)}
                                    onChange={() => toggleSubmissionSelection(submission.id)}
                                    className="rounded border-slate-300"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-slate-700">{submission.candidate_name}</div>
                                  {submission.candidate_no && (
                                    <div className="text-xs text-slate-500">Batch- {submission.candidate_no}</div>
                                  )}
                                  {submission.assigned_by && (
                                    <div className="text-xs text-purple-600 mt-1">
                                      📌 Assigned by {submission.assigned_by}
                                    </div>
                                  )}
                                  {submission.is_creator && (
                                    <div className="text-xs text-blue-600 mt-1">
                                      📝 Your Test
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">{submission.candidate_email}</td>
                                <td className="px-4 py-3 text-sm text-slate-500">
                                  {submission.submitted_at 
                                    ? new Date(submission.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                    : '-'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {getStatusBadge(submission.status)}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-2">
                                    {/* Check can_mark to determine if instructor can mark this submission */}
                                    {submission.can_mark ? (
                                      // Assigned instructor's view - can mark
                                      <>
                                        <Button
                                          size="sm"
                                          className="bg-blue-600 hover:bg-blue-700 text-white"
                                          onClick={() => navigate(`/instructor/mark/${submission.id}`)}
                                        >
                                          <ExternalLink className="w-3 h-3 mr-1" />
                                          Open
                                        </Button>
                                        {submission.status === 'published' && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-green-600 border-green-300 hover:bg-green-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewResult(submission.id, e);
                                            }}
                                            title="View Result Page"
                                          >
                                            <Eye className="w-3 h-3" />
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      // Creator's view - only Result button when published
                                      submission.status === 'published' ? (
                                        <Button
                                          size="sm"
                                          className="bg-green-600 hover:bg-green-700 text-white"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewResult(submission.id, e);
                                          }}
                                          title="View Result Page"
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          Result
                                        </Button>
                                      ) : (
                                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                          ⏳ Awaiting Marking
                                        </span>
                                      )
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Submissions - Mobile Cards */}
                      <div className="md:hidden space-y-3">
                        {subs.map((submission) => (
                          <div key={submission.id} className="bg-white rounded-lg border border-slate-200 p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-start space-x-3 flex-1">
                                <input 
                                  type="checkbox"
                                  checked={selectedSubmissions.includes(submission.id)}
                                  onChange={() => toggleSubmissionSelection(submission.id)}
                                  className="rounded border-slate-300 mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-slate-700 text-sm">{submission.candidate_name}</div>
                                  <div className="text-xs text-slate-500 truncate">{submission.candidate_email}</div>
                                  {submission.candidate_no && (
                                    <div className="text-xs text-slate-400 mt-1">Batch- {submission.candidate_no}</div>
                                  )}
                                  {submission.assigned_by && (
                                    <div className="text-xs text-purple-600 mt-1">
                                      📌 Assigned by {submission.assigned_by}
                                    </div>
                                  )}
                                  {submission.is_creator && (
                                    <div className="text-xs text-blue-600 mt-1">
                                      📝 Your Test
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-2">
                                {getStatusBadge(submission.status)}
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                              <div className="text-xs text-slate-500">
                                {submission.submitted_at 
                                  ? new Date(submission.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                  : '-'}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Check can_mark to determine if instructor can mark this submission */}
                                {submission.can_mark ? (
                                  <Button
                                    size="sm"
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                    onClick={() => navigate(`/instructor/mark/${submission.id}`)}
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Open
                                  </Button>
                                ) : (
                                  submission.status === 'published' ? (
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewResult(submission.id, e);
                                      }}
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      Result
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                      ⏳ Awaiting Marking
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bulk Actions */}
              {selectedSubmissions.length > 0 && (
                <div className="mt-6 flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-700">
                    {selectedSubmissions.length} submission(s) selected
                  </div>
                  <Button
                    onClick={handleBulkDelete}
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Other tabs */}
        {activeTab === 'batch' && (
          <Card className="glass-card">
            <CardContent className="py-12 text-center text-slate-500">
              Batch HW submissions coming soon...
            </CardContent>
          </Card>
        )}
          </>
        )}

        {/* Creation Tab Content - Only if user has permission */}
        {mainTab === 'creation' && user.can_create_tests && (
          <>
            <div className="grid gap-4 md:grid-cols-2 mb-6">
              {/* Class Test */}
              <Card className="glass-card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setIsCreateClassTestOpen(true)}>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'}}>
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">Class Test</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Create quick assessments for your class
                  </p>
                  <Button className="btn-primary w-full">
                    + Create Class Test
                  </Button>
                </CardContent>
              </Card>

              {/* Batch Homework */}
              <Card className="glass-card hover:shadow-lg transition-shadow opacity-50">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-slate-300">
                    <FileText className="w-8 h-8 text-slate-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">Batch Homework</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Assign homework to batches
                  </p>
                  <Button disabled className="w-full bg-slate-300 text-slate-500">
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Class Tests List */}
            <Card className="glass-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-slate-700 text-xl">My Class Tests</CardTitle>
                <span className="text-sm text-slate-500">{tests.filter(test => test.type === 'class_test').length} tests</span>
              </CardHeader>
              <CardContent>
                {tests.filter(test => test.type === 'class_test').length === 0 ? (
                  <div className="py-8 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                    <p className="text-sm">No class tests created yet</p>
                    <p className="text-xs mt-2">Click the &quot;Create Class Test&quot; card above to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tests.filter(test => test.type === 'class_test').map((test) => {
                      const now = new Date();
                      const startDate = test.start_date ? new Date(test.start_date) : null;
                      const endDate = test.end_date ? new Date(test.end_date) : null;
                      const isLocked = test.is_locked || (endDate && now > endDate);
                      
                      let lockStatus = '🔓 Unlocked';
                      let statusColor = 'text-green-600 bg-green-50 border-green-200';
                      
                      if (test.is_locked) {
                        lockStatus = '🔒 Locked';
                        statusColor = 'text-red-600 bg-red-50 border-red-200';
                      } else if (endDate && now > endDate) {
                        lockStatus = '🔒 Locked (Expired)';
                        statusColor = 'text-red-600 bg-red-50 border-red-200';
                      } else if (startDate && now < startDate) {
                        lockStatus = '⏰ Scheduled';
                        statusColor = 'text-amber-600 bg-amber-50 border-amber-200';
                      }
                      
                      const publicLink = test.public_link ? `${window.location.origin}/test/${test.public_link}` : null;
                      
                      return (
                        <div key={test.id} className="border border-slate-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow bg-white">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                <h3 className="font-semibold text-slate-800 break-words">{test.title}</h3>
                                <span className={`text-xs px-2 py-1 rounded-full border self-start ${statusColor}`}>
                                  {lockStatus}
                                </span>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-slate-600 mb-2">
                                <span className="break-words">📝 {test.task_configuration === 'full' ? 'Task 1 + 2' : test.task_configuration === 'task_1_only' ? 'Task 1 Only' : 'Task 2 Only'}</span>
                                <span>⏱️ {test.duration_minutes} mins</span>
                              </div>
                              {(startDate || endDate) && (
                                <div className="text-xs text-slate-500 space-y-1">
                                  {startDate && <div className="break-words">🔓 Unlock: {new Date(startDate).toLocaleString('en-GB')}</div>}
                                  {endDate && <div className="break-words">🔒 Lock: {new Date(endDate).toLocaleString('en-GB')}</div>}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {publicLink && (
                            <div className="mb-3 p-2 bg-slate-50 rounded border border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                              <input 
                                type="text" 
                                value={publicLink} 
                                readOnly 
                                className="flex-1 text-xs bg-transparent border-none outline-none text-slate-600 break-all min-w-0"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  navigator.clipboard.writeText(publicLink);
                                  toast.success('Link copied!');
                                }}
                                className="text-xs w-full sm:w-auto"
                              >
                                📋 Copy
                              </Button>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 sm:flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="btn-primary text-xs sm:text-sm"
                              onClick={() => {
                                setSelectedTest(test);
                                setNewClassTest({
                                  ...test,
                                  unlock_time: test.start_date ? new Date(test.start_date).toISOString().slice(0, 16) : '',
                                  lock_time: test.end_date ? new Date(test.end_date).toISOString().slice(0, 16) : ''
                                });
                                setIsCreateClassTestOpen(true);
                              }}
                            >
                              <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs sm:text-sm"
                              onClick={() => navigate(`/test-preview/${test.id}`)}
                            >
                              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Preview
                            </Button>
                            {/* Lock/Unlock Toggle Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              className={`text-xs sm:text-sm ${test.is_locked ? "text-green-600 border-green-300 hover:bg-green-50" : "text-red-600 border-red-300 hover:bg-red-50"}`}
                              onClick={() => {
                                const message = test.is_locked 
                                  ? 'Are you sure you want to unlock this test? Students will be able to take it again.'
                                  : 'Are you sure you want to lock this test? Students will no longer be able to take it.';
                                if (window.confirm(message)) {
                                  handleToggleLock(test.id, test.is_locked);
                                }
                              }}
                              title={test.is_locked ? "Unlock test" : "Lock test"}
                            >
                              {test.is_locked ? <Unlock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <Lock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
                              {test.is_locked ? 'Unlock' : 'Lock'}
                            </Button>
                            {/* Delete button */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50 text-xs sm:text-sm"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this test? This action cannot be undone. If there are unpublished submissions, you must mark and publish them first.')) {
                                  handleDeleteTest(test.id);
                                }
                              }}
                              title="Delete test"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Create Class Test Dialog */}
        <Dialog open={isCreateClassTestOpen} onOpenChange={setIsCreateClassTestOpen}>
          <DialogContent className="glass-card border-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-slate-700">{selectedTest ? 'Edit Class Test' : 'Create Class Test'}</DialogTitle>
              <DialogDescription className="text-slate-500">
                Create a flexible class test with Task 1, Task 2, or both
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Test Title */}
              <div className="space-y-2">
                <Label className="text-slate-700">Test Title*</Label>
                <Input
                  placeholder="e.g., Class Test - Week 5"
                  value={newClassTest.title}
                  onChange={(e) => setNewClassTest({ ...newClassTest, title: e.target.value })}
                  className="input-modern"
                />
              </div>

              {/* Task Configuration */}
              <div className="space-y-2">
                <Label className="text-slate-700">Task Configuration*</Label>
                <RadioGroup 
                  value={newClassTest.task_configuration} 
                  onValueChange={(value) => setNewClassTest({ ...newClassTest, task_configuration: value })}
                >
                  <div className="flex items-center space-x-2 p-3 border rounded-md hover:bg-slate-50">
                    <RadioGroupItem value="full" id="full" />
                    <Label htmlFor="full" className="cursor-pointer flex-1">
                      <span className="font-semibold">Full Test</span>
                      <span className="text-xs text-slate-500 ml-2">(Task 1 + Task 2)</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-md hover:bg-slate-50">
                    <RadioGroupItem value="task_1_only" id="task1" />
                    <Label htmlFor="task1" className="cursor-pointer flex-1">
                      <span className="font-semibold">Task 1 Only</span>
                      <span className="text-xs text-slate-500 ml-2">(150 words)</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-md hover:bg-slate-50">
                    <RadioGroupItem value="task_2_only" id="task2" />
                    <Label htmlFor="task2" className="cursor-pointer flex-1">
                      <span className="font-semibold">Task 2 Only</span>
                      <span className="text-xs text-slate-500 ml-2">(250 words)</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Task 1 Question (if needed) */}
              {(newClassTest.task_configuration === 'full' || newClassTest.task_configuration === 'task_1_only') && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Question*</Label>
                    
                    {/* Formatting Toolbar */}
                    <div className="flex gap-2 border border-slate-200 rounded-t-lg p-2 bg-slate-50">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertFormatting('task1_prompt', 'bold')}
                        className="h-8 w-8 p-0"
                        title="Bold"
                      >
                        <strong>B</strong>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertFormatting('task1_prompt', 'italic')}
                        className="h-8 w-8 p-0"
                        title="Italic"
                      >
                        <em>I</em>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertFormatting('task1_prompt', 'underline')}
                        className="h-8 w-8 p-0"
                        title="Underline"
                      >
                        <u>U</u>
                      </Button>
                      <span className="text-xs text-slate-500 ml-2 flex items-center">Select text and click formatting button</span>
                    </div>

                    <textarea
                      id="task1_prompt"
                      placeholder="Enter Task 1 question/prompt..."
                      value={newClassTest.task1_prompt}
                      onChange={(e) => setNewClassTest({ ...newClassTest, task1_prompt: e.target.value })}
                      className="input-modern min-h-[120px] rounded-t-none"
                      rows={5}
                    />
                    <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                      <p className="font-semibold mb-1">Formatting Preview:</p>
                      <p>• **Bold text** → <strong>Bold text</strong></p>
                      <p>• *Italic text* → <em>Italic text</em></p>
                      <p>• __Underline__ → <u>Underline</u></p>
                    </div>
                  </div>
                  
                  {/* Task 1 Image Upload */}
                  <div className="space-y-2">
                    <Label className="text-slate-700">Image (Optional)</Label>
                    {!newClassTest.task1_image ? (
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setNewClassTest({ 
                                  ...newClassTest, 
                                  task1_image: reader.result,
                                  task1_image_width: 500,
                                  task1_image_height: 300
                                });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="task1-image-upload"
                        />
                        <label htmlFor="task1-image-upload" className="cursor-pointer">
                          <Upload className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload image</p>
                          <p className="text-xs text-slate-400 mt-1">PNG, JPG up to 5MB</p>
                        </label>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="relative border rounded-lg p-4 bg-slate-50">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setNewClassTest({ ...newClassTest, task1_image: null, task1_image_width: 500, task1_image_height: 300 })}
                            className="absolute top-2 right-2 text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                          <img 
                            src={newClassTest.task1_image} 
                            alt="Task 1 preview"
                            style={{
                              width: `${newClassTest.task1_image_width || 500}px`,
                              height: `${newClassTest.task1_image_height || 300}px`,
                              objectFit: 'contain'
                            }}
                            className="mx-auto"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">Width (px)</Label>
                            <Input
                              type="number"
                              value={newClassTest.task1_image_width || 500}
                              onChange={(e) => setNewClassTest({ ...newClassTest, task1_image_width: parseInt(e.target.value) || 500 })}
                              className="input-modern"
                              min="100"
                              max="1200"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Height (px)</Label>
                            <Input
                              type="number"
                              value={newClassTest.task1_image_height || 300}
                              onChange={(e) => setNewClassTest({ ...newClassTest, task1_image_height: parseInt(e.target.value) || 300 })}
                              className="input-modern"
                              min="100"
                              max="800"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Task 2 Question (if needed) */}
              {(newClassTest.task_configuration === 'full' || newClassTest.task_configuration === 'task_2_only') && (
                <div className="space-y-2">
                  <Label className="text-slate-700">Task 2 Question*</Label>
                  
                  {/* Formatting Toolbar */}
                  <div className="flex gap-2 border border-slate-200 rounded-t-lg p-2 bg-slate-50">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('task2_prompt', 'bold')}
                      className="h-8 w-8 p-0"
                      title="Bold"
                    >
                      <strong>B</strong>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('task2_prompt', 'italic')}
                      className="h-8 w-8 p-0"
                      title="Italic"
                    >
                      <em>I</em>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('task2_prompt', 'underline')}
                      className="h-8 w-8 p-0"
                      title="Underline"
                    >
                      <u>U</u>
                    </Button>
                    <span className="text-xs text-slate-500 ml-2 flex items-center">Select text and click formatting button</span>
                  </div>

                  <textarea
                    id="task2_prompt"
                    placeholder="Enter Task 2 question/prompt..."
                    value={newClassTest.task2_prompt}
                    onChange={(e) => setNewClassTest({ ...newClassTest, task2_prompt: e.target.value })}
                    className="input-modern min-h-[120px] rounded-t-none"
                    rows={5}
                  />
                  <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                    <p className="font-semibold mb-1">Formatting Preview:</p>
                    <p>• **Bold text** → <strong>Bold text</strong></p>
                    <p>• *Italic text* → <em>Italic text</em></p>
                    <p>• __Underline__ → <u>Underline</u></p>
                  </div>
                </div>
              )}

              {/* Duration */}
              <div className="space-y-2">
                <Label className="text-slate-700">Duration (minutes)*</Label>
                <Input
                  type="number"
                  value={newClassTest.duration_minutes}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 10;
                    const clamped = Math.min(Math.max(val, 10), 120);
                    setNewClassTest({ ...newClassTest, duration_minutes: clamped });
                  }}
                  className="input-modern"
                  min="10"
                  max="120"
                  placeholder="10-120 minutes"
                />
                <p className="text-xs text-slate-500">Flexible duration: 10 to 120 minutes. Red alert shows 10 minutes before time ends.</p>
              </div>

              {/* Unlock Time */}
              <div className="space-y-2">
                <Label className="text-slate-700">Unlock Time (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={newClassTest.unlock_time}
                  onChange={(e) => setNewClassTest({ ...newClassTest, unlock_time: e.target.value })}
                  className="input-modern"
                />
                <p className="text-xs text-slate-500">When students can start taking this test. Leave empty for immediate access.</p>
              </div>

              {/* Lock Time */}
              <div className="space-y-2">
                <Label className="text-slate-700">Lock Time (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={newClassTest.lock_time}
                  onChange={(e) => setNewClassTest({ ...newClassTest, lock_time: e.target.value })}
                  className="input-modern"
                />
                <p className="text-xs text-slate-500">When test will automatically lock. Students cannot start after this time.</p>
              </div>

              {/* Assign to Other Instructors */}
              {instructors.length > 1 && (
                <div className="space-y-2">
                  <Label className="text-slate-700">Assign to Other Instructors (Optional)</Label>
                  <div className="border border-slate-200 rounded-md p-3 max-h-48 overflow-y-auto space-y-2 bg-slate-50">
                    {instructors.filter(inst => inst.id !== user.id).map((instructor) => (
                      <div key={instructor.id} className="flex items-center space-x-2 p-2 hover:bg-slate-100 rounded">
                        <Checkbox
                          id={`instructor-${instructor.id}`}
                          checked={newClassTest.assigned_instructors.includes(instructor.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewClassTest({
                                ...newClassTest,
                                assigned_instructors: [...newClassTest.assigned_instructors, instructor.id]
                              });
                            } else {
                              setNewClassTest({
                                ...newClassTest,
                                assigned_instructors: newClassTest.assigned_instructors.filter(id => id !== instructor.id)
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`instructor-${instructor.id}`} className="cursor-pointer flex-1">
                          <span className="font-medium text-slate-700">{instructor.name}</span>
                          <span className="text-xs text-slate-500 ml-2">{instructor.email}</span>
                        </Label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    Selected instructors will be able to mark this test. You are automatically assigned as creator.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button onClick={handleCreateClassTest} className="btn-primary">
                {selectedTest ? 'Update Test' : 'Create Class Test'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Dialog */}
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogContent className="glass-card border-none max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-slate-700">Assign Test to Batches</DialogTitle>
              <DialogDescription className="text-slate-500">
                {selectedTest?.title}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Batch Selection */}
              <div className="space-y-2">
                <Label className="text-slate-700">Select Batches*</Label>
                <div className="border border-slate-200 rounded-md p-3 max-h-60 overflow-y-auto space-y-2">
                  {batches.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No batches available</p>
                  ) : (
                    batches.map((batch) => (
                      <div key={batch.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`batch-${batch.id}`}
                          checked={assignmentData.assigned_batches.includes(batch.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAssignmentData({
                                ...assignmentData,
                                assigned_batches: [...assignmentData.assigned_batches, batch.id]
                              });
                            } else {
                              setAssignmentData({
                                ...assignmentData,
                                assigned_batches: assignmentData.assigned_batches.filter(id => id !== batch.id)
                              });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <label htmlFor={`batch-${batch.id}`} className="text-sm text-slate-700 cursor-pointer">
                          {batch.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-slate-500">{assignmentData.assigned_batches.length} batch(es) selected</p>
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <Label className="text-slate-700">Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={assignmentData.start_date}
                  onChange={(e) => setAssignmentData({ ...assignmentData, start_date: e.target.value })}
                  className="input-modern"
                />
                <p className="text-xs text-slate-500">Test will be visible to students from this date</p>
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <Label className="text-slate-700">End Date (Optional)</Label>
                <Input
                  type="date"
                  value={assignmentData.end_date}
                  onChange={(e) => setAssignmentData({ ...assignmentData, end_date: e.target.value })}
                  className="input-modern"
                />
                <p className="text-xs text-slate-500">Test will expire after this date</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignTest} className="btn-primary">
                Save Assignment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default InstructorDashboard;
